import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type AccountContact, accountApi } from '@/api/commercial/accounts';
import type { LocalContact } from '@/context/create-account-flow';
import { useToast } from '@/hooks/use-toast';

interface UseContactMutationsProps {
  accountId?: string;
  contacts: AccountContact[];
  onContactsChange: (contacts: AccountContact[]) => void;
}

export function useContactMutations({
  accountId,
  contacts,
  onContactsChange,
}: UseContactMutationsProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const createContact = useMutation({
    mutationFn: async (data: LocalContact) => {
      if (!accountId) throw new Error('Account ID is required');
      return accountApi.createContact({ ...data, accountId });
    },
    onSuccess: (newContact) => {
      toast({
        title: 'Contato adicionado',
        description: 'O contato foi adicionado com sucesso!',
      });
      onContactsChange([...contacts, newContact]);
      queryClient.invalidateQueries({ queryKey: ['findContacts', accountId] });
    },
    onError: () => {
      toast({
        title: 'Erro',
        description: 'Ocorreu um erro ao adicionar o contato. Tente novamente.',
        variant: 'destructive',
      });
    },
  });

  const updateContact = useMutation({
    mutationFn: async ({ contactId, data }: { contactId: string; data: LocalContact }) => {
      return accountApi.updateContact(contactId, { ...data, accountId });
    },
    onSuccess: (updatedContact) => {
      toast({
        title: 'Contato atualizado',
        description: 'O contato foi atualizado com sucesso!',
      });
      onContactsChange(contacts.map((c) => (c.id === updatedContact.id ? updatedContact : c)));
      queryClient.invalidateQueries({ queryKey: ['findContacts', accountId] });
    },
    onError: () => {
      toast({
        title: 'Erro',
        description: 'Ocorreu um erro ao atualizar o contato. Tente novamente.',
        variant: 'destructive',
      });
    },
  });

  return { createContact, updateContact };
}
